import {Component, Input, EventEmitter, Output} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import {CreditSafeService, CreditSafeSearchOrganizationResult, Signatory} from './credit-safe.service';
import {CommonModule} from "@angular/common";

// Define the interface for signatory dropdown
export interface ISignatoryDropdownDTO {
  label: string;
  value: string;
}

@Component({
  selector: 'app-credit-safe-info-fetcher',
  standalone: true,
  imports: [
      CommonModule,
      ReactiveFormsModule,
  ],
  templateUrl: './credit-safe-info-fetcher.component.html',
  styleUrl: './credit-safe-info-fetcher.component.scss'
})

export class CreditSafeInfoFetcherComponent {
  @Input() uniqueId: string;
  @Output() personFound = new EventEmitter<any>();
  @Output() organizationFound = new EventEmitter<any>();
  @Output() signatorySelected = new EventEmitter<any>();
  @Output() searchInformationNotFound = new EventEmitter<void>();

  searchPersonForm: FormGroup;
  searchOrganizationForm: FormGroup;
  signatorySelectionForm: FormGroup;

  isLoading = false;
  organizationSearchSuccessful = false;
  signatories: ISignatoryDropdownDTO[] = [];

  private originalSignatories: Signatory[] = [];
  private destroy$ = new Subject<void>();

  constructor(
      private fb: FormBuilder,
      public creditSafeService: CreditSafeService
  ) {

    this.searchPersonForm = this.fb.group({
      personalNumberToSearch: ['', [Validators.required, Validators.pattern('^[0-9]{10}$')]]
    });

    this.searchOrganizationForm = this.fb.group({
      organizationNumberToSearch: ['', [Validators.required, Validators.pattern('^[0-9]{10}$')]]
    });

    this.signatorySelectionForm = this.fb.group({
      selectedSignatory: ['', [Validators.required]]
    });

  }

  showPrivatePersonForm(): boolean {
    return this.creditSafeService.getSkoogsConfigurationType(this.uniqueId) === 'PRIVATE';
  }

  showOrganizationForm(): boolean {
    return ['ORG_T5', 'ORG_T4', 'ORG_T3', 'ORG_T2', 'ORG_T1'].includes(
        this.creditSafeService.getSkoogsConfigurationType(this.uniqueId)
    );
  }

  getPrivatePersonInformation(): void {
    if (this.searchPersonForm.invalid) {
      return;
    }

    const personalNumber = this.searchPersonForm.get('personalNumberToSearch')?.value;

    if (!personalNumber) {
      return;
    }

    this.isLoading = true;

    this.creditSafeService.searchPerson(personalNumber).pipe(
        takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.isLoading = false;

        if (response.status === 'success') {
          response.result.social_security_number = personalNumber;
          this.personFound.emit(response.result);
        } else {
          this.searchInformationNotFound.emit();
        }
      },
      error: (error) => {
        this.isLoading = false;
      }
    });
  }

  getOrganizationInformation(): void {
    if (this.searchOrganizationForm.invalid) {
      return;
    }

    const organizationNumber = this.searchOrganizationForm.get('organizationNumberToSearch')?.value;

    if (!organizationNumber) {
      return;
    }

    this.isLoading = true;
    this.organizationSearchSuccessful = false;
    this.signatories = [];

    this.creditSafeService.searchOrganization(organizationNumber).pipe(
        takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.isLoading = false;

        if (response.status === 'success') {
          this.organizationSearchSuccessful = true;
          this.organizationFound.emit(response.result);
          this.processSignatoriesFromResponse(response.result);
        } else {
          this.searchInformationNotFound.emit();
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.organizationSearchSuccessful = false;
        this.searchInformationNotFound.emit();
      }
    });
  }

  private processSignatoriesFromResponse(orgSearchResult: CreditSafeSearchOrganizationResult): void {
    if (orgSearchResult?.signatories && Array.isArray(orgSearchResult.signatories)) {
      // Store the original signatory objects
      this.originalSignatories = [...orgSearchResult.signatories];

      this.signatories = orgSearchResult.signatories.map((signatory: Signatory) => ({
        label: `${signatory.last_name}, ${signatory.first_name}`,
        value: signatory.social_security_number
      }));
    }
  }

  /**
   * Handle signatory selection
   */
  onSignatoryChange(): void {
    const selectedValue = this.signatorySelectionForm.get('selectedSignatory')?.value;
    if (selectedValue) {
      const selectedSignatory = this.originalSignatories.find(s => s.social_security_number === selectedValue);
      if (selectedSignatory) {
        this.signatorySelected.emit(selectedSignatory);
      }
    }
  }

}
